import { useSelector } from 'react-redux';
import { useGetUserDetailsQuery } from '../user/userApi';
import { useMemo } from 'react';

export const normalizeSignatureEmail = (value) => {
	if (typeof value !== 'string') return '';
	return value.trim().toLowerCase();
};

export const getSignatureEmailLabel = ({ email, isPrimary = false, isDelegated = false }) => {
	const normalizedEmail = normalizeSignatureEmail(email);
	if (!normalizedEmail) return '';
	if (isPrimary) {
		return `${normalizedEmail} (primary)`;
	}
	if (isDelegated) {
		return `${normalizedEmail} (delegated)`;
	}
	return normalizedEmail;
};

const toAvailableEmailOption = ({ email, isPrimary = false, isDelegated = false }) => {
	const normalizedEmail = normalizeSignatureEmail(email);
	if (!normalizedEmail) return null;

	return {
		id: normalizedEmail,
		email: normalizedEmail,
		normalizedEmail,
		isPrimary,
		isDelegated,
		label: getSignatureEmailLabel({ email: normalizedEmail, isPrimary, isDelegated }),
		name: normalizedEmail,
		value: normalizedEmail,
	};
};

/**
 * Builds the ordered list of email addresses that can own a saved user signature.
 * The primary email is always first, and delegated addresses follow alphabetically.
 */
export const buildAvailableSignatureEmailOptions = ({
	primaryEmail,
	delegatedEmails = [],
} = {}) => {
	const primaryOption = toAvailableEmailOption({
		email: primaryEmail,
		isPrimary: true,
		isDelegated: false,
	});
	const delegatedOptions = Array.from(
		new Set(
			(delegatedEmails ?? [])
				.map((delegatedEmail) => normalizeSignatureEmail(delegatedEmail))
				.filter(Boolean),
		),
	)
		.filter((delegatedEmail) => delegatedEmail !== primaryOption?.normalizedEmail)
		.sort((left, right) => left.localeCompare(right))
		.map((delegatedEmail) =>
			toAvailableEmailOption({
				email: delegatedEmail,
				isPrimary: false,
				isDelegated: true,
			}),
		)
		.filter(Boolean);

	return [primaryOption, ...delegatedOptions].filter(Boolean);
};

/**
 * Builds the ordered list of email addresses that can own a saved user signature.
 * Defaults to the logged-in user; pass userId (the target user's Firestore doc id)
 * to author for another user.
 */
export const getAvailableSignatureEmailOptions = (userId) => {
	const account = useSelector((state) => state.msalAccount.account);
	const email = account?.username;
	const hasTarget = userId != null;

	const { data: userDetails } = useGetUserDetailsQuery(
		hasTarget ? { userId } : { email },
		{
			skip: !hasTarget && !email,
		},
	);

	const primaryEmail = userDetails?.email;
	const delegatedEmails = useMemo(
		() => userDetails?.delegatedEmails ?? [],
		[userDetails?.delegatedEmails],
	);

	return useMemo(
		() =>
			buildAvailableSignatureEmailOptions({
				primaryEmail,
				delegatedEmails,
			}),
		[primaryEmail, delegatedEmails],
	);
};

export const findAvailableSignatureEmailOption = (emailOptions, email) => {
	const normalizedEmail = normalizeSignatureEmail(email);
	if (!normalizedEmail) return null;
	return (
		(emailOptions ?? []).find((option) => option.normalizedEmail === normalizedEmail) ?? null
	);
};

export const getSignatureOptionId = ({ source, email }) => {
	const normalizedEmail = normalizeSignatureEmail(email);
	if (!source || !normalizedEmail) return '';
	return `${source}:${normalizedEmail}`;
};

export const getSignatureOptionMatchKey = (option) => {
	if (!option) return '';
	return option.id ?? getSignatureOptionId(option);
};

const buildSignatureOption = ({
	source,
	emailOption,
	html = '',
	legacySignatureUrl = '',
	recordId = '',
}) => {
	if (!emailOption?.normalizedEmail || !source) return null;

	return {
		id: getSignatureOptionId({ source, email: emailOption.normalizedEmail }),
		recordId,
		source,
		email: emailOption.email,
		normalizedEmail: emailOption.normalizedEmail,
		isPrimary: Boolean(emailOption.isPrimary),
		isDelegated: Boolean(emailOption.isDelegated),
		label: emailOption.label,
		name: emailOption.label,
		html,
		signature: legacySignatureUrl,
	};
};

/**
 * Normalizes stored Unlayer signature records into the selector option shape used by compose.
 */
export const buildUnlayerSignatureOptions = ({ emailOptions = [], signatureRecords = [] }) => {
	const emailOptionMap = new Map(
		(emailOptions ?? []).map((option) => [option.normalizedEmail, option]),
	);

	return (signatureRecords ?? [])
		.map((record) => {
			const emailOption = emailOptionMap.get(normalizeSignatureEmail(record?.email));
			if (!emailOption || typeof record?.html !== 'string' || record.html.length === 0) {
				return null;
			}
			return buildSignatureOption({
				source: 'unlayer',
				emailOption,
				html: record.html,
				recordId: record.id,
			});
		})
		.filter(Boolean);
};

/**
 * Normalizes the legacy signature url records into the same selector option shape.
 */
export const buildLegacySignatureOptions = ({ emailOptions = [], legacySignatures = [] }) => {
	const emailOptionMap = new Map(
		(emailOptions ?? []).map((option) => [option.normalizedEmail, option]),
	);

	return (legacySignatures ?? [])
		.map((record) => {
			const emailOption = emailOptionMap.get(
				normalizeSignatureEmail(record?.email ?? record?.name),
			);
			if (
				!emailOption ||
				typeof record?.signature !== 'string' ||
				record.signature.length === 0
			) {
				return null;
			}
			return buildSignatureOption({
				source: 'legacy',
				emailOption,
				legacySignatureUrl: record.signature,
				recordId: record?.id ?? record?.name ?? emailOption.normalizedEmail,
			});
		})
		.filter(Boolean);
};
